// Pipeline UNICO "documento HTML -> JPG base64" pro envio por WhatsApp.
//
// Por que existe: esta mecanica nasceu enterrada dentro do `gerarExtratoJpg` do extrato de Contas
// a Receber, misturada com o LAYOUT daquele documento. Quando o extrato da Venda CM precisou da
// mesma saida, copiar teria criado a terceira copia do mesmo codigo (o HTML do extrato do CR ja
// vive em duas — front e backend, pro robo que responde "EXTRATO" no WhatsApp). Extraido aqui, o
// layout de cada documento fica sendo a UNICA coisa que cada um escreve.
//
// O WhatsApp recebe IMAGEM, nao PDF: o cliente ve o conteudo na propria thumbnail, sem baixar nada.

import html2canvas from 'html2canvas';

/**
 * Baixa uma imagem e devolve como dataURL. Necessario porque o html2canvas nao consegue pintar
 * `<img src="http...">` de outra origem no canvas (o canvas fica "sujo" e a captura falha); com
 * dataURL nao ha requisicao nenhuma na hora do render.
 */
export async function carregarImagemComoDataUrl(url: string): Promise<string | null> {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(typeof r.result === 'string' ? r.result : null);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

/**
 * Renderiza UMA pagina de documento HTML e devolve o JPG em base64 (sem o prefixo `data:`).
 *
 * O documento e desenhado num `<div>` fora da tela (nao num iframe): o html2canvas precisa que o
 * CSS esteja APLICADO no DOM, entao o `<style>` e o `<body>` sao extraidos do HTML e reinjetados.
 * Paginar e responsabilidade de quem chama — cada documento sabe o proprio criterio de quebra.
 */
export async function htmlParaJpg(html: string, larguraPx = 720): Promise<string> {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = `${larguraPx}px`;
  container.style.background = '#fff';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.color = '#000';
  container.style.padding = '16px';

  const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const styleHtml = styleMatch ? `<style>${styleMatch[1]}</style>` : '';
  const bodyHtml = bodyMatch ? bodyMatch[1]! : html;
  container.innerHTML = styleHtml + bodyHtml;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#fff', useCORS: true, logging: false });
    return canvas.toDataURL('image/jpeg', 0.92).split(',')[1] || '';
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Abre o documento numa janela e dispara o print. **Este caminho serve o PAPEL.**
 *
 * ⛔ **Nao conte com o "Salvar como PDF" do dialogo do Chrome** — ele trava em "Salvando..." pra
 * sempre, com a previa desenhada certa (defeito enganoso: quem imprime no papel nunca ve). Foram
 * DUAS tentativas de conserto pelo navegador, as duas fracassadas: largura de folha no documento
 * e Blob URL no lugar de `about:blank` (DA-417 — este comentario ja afirmou que a segunda tinha
 * resolvido; o extrato do Contas a Receber provou o contrario em 18/08/2026). Quem precisa de
 * ARQUIVO pede ao servidor, que renderiza com Puppeteer: `gerarExtratoPdf` (extrato do CR),
 * `POST /consignacao/:id/extrato-pdf`, DANFE/DANFCE, pedido de compra.
 *
 * O Blob fica porque resolveu a outra metade: com URL de verdade o `load` dispara pra valer (o
 * `document.write` nem sempre dispara). A URL e revogada tarde de proposito — revogar na mesma
 * pilha cancela o carregamento, a mesma armadilha do download de blob documentada no CLAUDE.md.
 */
export function imprimirHtml(html: string): void {
  // ⚠️ `<base>` OBRIGATORIO: num documento `blob:` o endereco relativo (`/uploads/foto.jpg`) nao
  // tem host pra resolver — a URL do blob e opaca, nao serve de base. Sem isto, TODA foto de
  // produto do extrato vira quadradinho quebrado no papel (regressao que entrou junto com a
  // troca de about:blank por Blob, onde o relativo ainda resolvia pela origem do abridor).
  const comBase = /<head[^>]*>/i.test(html)
    ? html.replace(/<head([^>]*)>/i, `<head$1><base href="${window.location.origin}/">`)
    : `<base href="${window.location.origin}/">${html}`;
  const url = URL.createObjectURL(new Blob([comBase], { type: 'text/html;charset=utf-8' }));
  const win = window.open(url, '_blank');
  if (!win) {
    URL.revokeObjectURL(url);
    alert('Bloqueador de popup ativo? Permita janelas pra este site.');
    return;
  }
  win.addEventListener('load', () => win.print(), { once: true });
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
